import { useAuthStore } from '@/stores/auth';
import { hasFeatureAccess, isCialInternalUser } from '@/utils/auth';
import type { FeatureKey } from '@/utils/auth';
import { isValidUuid, isCpfPattern, isCnpjPattern } from '@/utils/validators';
import { normalizeCpf, normalizeCnpj } from '@/utils/formatters';
import personApi from '@/api/modules/personApi';
import screeningInternationalApi from '@/api/modules/screeningInternationalApi';
import type { NavigationGuardNext, RouteLocationNormalized, RouteLocationRaw } from 'vue-router';
import { isOpenToPlatformCountry } from '@/platformCountry';

/**
 * Authentication guard for protected routes
 * Redirects to login if user is not authenticated
 */
export const requireAuth = (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): void => {
  const authStore = useAuthStore();

  // Wait for auth to be initialized before making decisions
  if (!authStore.isInitialized) {
    // If auth is still initializing, wait a bit and retry
    setTimeout(() => {
      requireAuth(to, _from, next);
    }, 100);
    return;
  }

  if (authStore.isAuthenticated) {
    next();
  } else {
    // Redirect to login page with return URL
    next({
      name: 'login',
      query: { redirect: to.fullPath },
    });
  }
};

/**
 * Guest guard for login/register pages
 * Redirects to home if user is already authenticated
 */
export const requireGuest = (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): void => {
  const authStore = useAuthStore();

  // Wait for auth to be initialized before making decisions
  if (!authStore.isInitialized) {
    // If auth is still initializing, wait a bit and retry
    setTimeout(() => {
      requireGuest(to, _from, next);
    }, 100);
    return;
  }

  if (authStore.isAuthenticated) {
    // Redirect to home or intended page
    const redirectPath = (to.query.redirect as string) || '/';
    next(redirectPath);
  } else {
    next();
  }
};

/**
 * Subscription guard for routes that serve account data.
 *
 * Compose it after `requireAuth`, e.g. `beforeEnter: [requireAuth, requireSubscription]`.
 * Authenticated users whose account has no active subscription are sent to the
 * dedicated gate page. The flag is a UX cache (`authStore.isSubscribed`) refreshed
 * on load; blocking happens only on an explicit `false`, so an unknown/failed
 * check (`null`) fails open rather than locking users out.
 */
export const requireSubscription = (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): void => {
  const authStore = useAuthStore();

  if (!authStore.isInitialized) {
    setTimeout(() => {
      requireSubscription(to, _from, next);
    }, 100);
    return;
  }

  // Not authenticated: defer to the auth guard's login redirect.
  if (!authStore.isAuthenticated) {
    next({
      name: 'login',
      query: { redirect: to.fullPath },
    });
    return;
  }

  if (authStore.isSubscribed === false) {
    next({ name: 'subscription-required' });
    return;
  }

  next();
};

/**
 * System Admin guard for system admin routes
 * Redirects if user is not a system admin
 */
export const requireSystemAdmin = (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): void => {
  const authStore = useAuthStore();

  if (!authStore.isInitialized) {
    setTimeout(() => {
      requireSystemAdmin(to, _from, next);
    }, 100);
    return;
  }

  if (authStore.isAuthenticated && authStore.isSystemAdmin) {
    next();
  } else if (authStore.isAuthenticated) {
    // User is logged in but not a system admin
    // Redirect to a 'forbidden' page or home
    next({ name: 'home' }); // Or a dedicated 403 page
  } else {
    // User is not logged in
    next({
      name: 'login',
      query: { redirect: to.fullPath },
    });
  }
};

/**
 * Feature toggle guard factory for routes gated by a backend
 * feature toggle (see `backend/config/feature_toggles.py`). Users whose
 * `enabled_features` doesn't include `feature` are redirected to a shared
 * "coming soon" placeholder, forwarding the route's `meta.comingSoon` copy via query.
 */
export const requireFeature = (feature: FeatureKey) => (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): void => {
  const authStore = useAuthStore();

  if (!authStore.isInitialized) {
    setTimeout(() => {
      requireFeature(feature)(to, _from, next);
    }, 100);
    return;
  }

  if (!authStore.isAuthenticated) {
    next({
      name: 'login',
      query: { redirect: to.fullPath },
    });
    return;
  }

  if (hasFeatureAccess(authStore.user, feature)) {
    next();
    return;
  }

  // Authenticated but the feature isn't enabled for this user — show the "coming soon" page.
  const comingSoon = to.meta.comingSoon as { title?: string; description?: string } | undefined;
  next({
    name: 'coming-soon',
    query: {
      ...(comingSoon?.title ? { title: comingSoon.title } : {}),
      ...(comingSoon?.description ? { description: comingSoon.description } : {}),
    },
  });
};

/**
 * Feature toggle guard factory that also admits CIAL internal users.
 *
 * Same behavior as `requireFeature`, but a CIAL internal user (by email) is
 * always let through regardless of the account's feature toggle. Mirrors the
 * sidebar's `hasFeatureAccessOrIsCialUser` visibility so a shown nav link and
 * its route stay in lockstep. On failure, forwards to the shared "coming soon"
 * placeholder using the route's `meta.comingSoon` copy.
 */
export const requireFeatureOrCial = (feature: FeatureKey) => (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): void => {
  const authStore = useAuthStore();

  if (!authStore.isInitialized) {
    setTimeout(() => {
      requireFeatureOrCial(feature)(to, _from, next);
    }, 100);
    return;
  }

  if (!authStore.isAuthenticated) {
    next({
      name: 'login',
      query: { redirect: to.fullPath },
    });
    return;
  }

  if (isCialInternalUser(authStore.user?.email) || hasFeatureAccess(authStore.user, feature)) {
    next();
    return;
  }

  // Authenticated, not CIAL, and the feature isn't enabled — show the "coming soon" page.
  const comingSoon = to.meta.comingSoon as { title?: string; description?: string } | undefined;
  next({
    name: 'coming-soon',
    query: {
      ...(comingSoon?.title ? { title: comingSoon.title } : {}),
      ...(comingSoon?.description ? { description: comingSoon.description } : {}),
    },
  });
};

/*
 * Canonicalization redirects deliberately do NOT pass `replace: true`.
 *
 * A guard redirect inherits the flag of the navigation it interrupted, and vue-router already
 * replaces on the very first navigation (`finalizeNavigation`: `if (replace || isFirstNavigation)`).
 * So `replace` buys nothing for someone who loads a non-canonical URL directly — that case is
 * covered — while for an in-app `push` it overwrites the entry the user came FROM, because the
 * interrupted navigation was never committed. That silently deletes the referring page from
 * history, so Back skips straight past it (e.g. the parecer queue vanishing when a row was
 * opened). Pushing is correct here: the non-canonical URL never enters history either way, so
 * there is no redirect loop to Back into.
 */
/**
 * Person identifier resolution guard
 * Resolves CPF to UUID and redirects to canonical URL
 */
/**
 * Global Screening guard. Shows the "temporarily unavailable" page when the
 * backend reports Spearwatch can't be called (disabled or credentials blocked).
 * Fails closed: if the status check itself fails, the page stays blocked.
 */
export const requireGlobalScreeningAvailable = async (): Promise<true | RouteLocationRaw> => {
  const unavailableRoute: RouteLocationRaw = {
    name: 'coming-soon',
    query: {
      title: 'Triagem Global temporariamente indisponível',
      description: 'Este serviço está temporariamente indisponível. Estará disponível novamente em breve.',
    },
  };

  try {
    const { available } = await screeningInternationalApi.getStatus();
    return available ? true : unavailableRoute;
  } catch {
    return unavailableRoute;
  }
};

export const resolvePersonIdentifier = async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> => {
  const rawParam = to.params.uuid as string;

  // 1. Check if it's already a valid UUID
  if (isValidUuid(rawParam)) {
    return next();
  }

  // 2. Not a UUID - check for valid CPF patterns
  if (isCpfPattern(rawParam)) {
    const cleanedCPF = normalizeCpf(rawParam);
    try {
      console.log(`[Router] Resolving Person CPF "${rawParam}" to UUID...`);
      const response = await personApi.lookup(cleanedCPF, 0, 1);
      const resolvedUuid = response.data[0]?.uuid;
      
      if (resolvedUuid) {
        console.log(`[Router] Resolved CPF "${rawParam}" -> UUID "${resolvedUuid}"`);
        // See the note above `resolvePersonIdentifier` on why this is not a `replace`.
        return next({
          name: to.name ?? undefined,
          params: { ...to.params, uuid: resolvedUuid },
          query: to.query,
        });
      }
      console.warn(`[Router] CPF "${cleanedCPF}" not found in system.`);
    } catch (error) {
      console.error(`[Router] Error resolving CPF "${cleanedCPF}":`, error);
    }
  }

  // 3. Invalid identifier - Redirect to NotFound with context
  console.warn(`[Router] Invalid Person identifier: "${rawParam}"`);
  return next({ 
    name: 'NotFound', 
    query: { type: 'person', id: rawParam } 
  });
};

/**
 * Company identifier resolution guard
 * Normalizes 14-digit CNPJ to 8-digit Base CNPJ and ensures clean URLs
 */
export const resolveCompanyIdentifier = (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): void => {
  const rawParam = to.params.cnpj as string;
  // 1. Check for 14-character Full CNPJ - redirect to 8-character version
  if (isCnpjPattern(rawParam, 'full')) {
    const cleanCnpj = normalizeCnpj(rawParam);
    const baseCnpj = cleanCnpj.slice(0, 8);
    console.log(`[Router] Normalizing Company CNPJ from 14 characters ("${rawParam}") to 8 characters ("${baseCnpj}")`);
    return next({
      name: to.name ?? undefined,
      params: { ...to.params, cnpj: baseCnpj },
      query: to.query,
    });
  }

  // 2. Check for 8-character Base CNPJ
  if (isCnpjPattern(rawParam, 'base')) {
    const cleanCnpj = normalizeCnpj(rawParam);

    // Redirect formatted or lowercase CNPJs to the canonical identifier.
    if (rawParam !== cleanCnpj) {
      console.log(`[Router] Cleaning Company identifier format: "${rawParam}" -> "${cleanCnpj}"`);
      return next({
        name: to.name ?? undefined,
        params: { ...to.params, cnpj: cleanCnpj },
        query: to.query,
      });
    }
    return next();
  }

  // 3. Invalid identifier - Redirect to NotFound with context
  console.warn(`[Router] Invalid Company identifier: "${rawParam}"`);
  return next({ 
    name: 'NotFound', 
    query: { type: 'company', id: rawParam } 
  });
};

const waitForAuthInitialization = (authStore: ReturnType<typeof useAuthStore>): Promise<void> =>
  new Promise((resolve) => {
    const check = () => (authStore.isInitialized ? resolve() : setTimeout(check, 100));
    check();
  });

/**
 * Global platform-country guard. A page is open only to the platform countries listed in its route
 * meta `platformCountries` — Brazil only when omitted — so pages are opened to other platforms one
 * by one, after checking they work there. Uses the signed-in account's platform (an account whose
 * platform isn't set reaches only 'any' pages). Signed-out visitors are left to the auth guards.
 */
export const requirePlatformCountry = async (to: RouteLocationNormalized): Promise<true | RouteLocationRaw> => {
  const authStore = useAuthStore();
  await waitForAuthInitialization(authStore);

  if (!authStore.isAuthenticated) return true;
  if (isOpenToPlatformCountry(to.meta.platformCountries, authStore.accountPlatformCountry)) return true;
  return { name: 'platform-unavailable' };
};
